import React from 'react'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCreateNewInstallationFormInitializer, useSavePlannedInstallationEm360Defaults } from 'src/Libs/installations/hooks'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Stack } from '@mui/system'
import { SettingInfoBox } from 'src/Elements/Data'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { createNewInstallation } from 'src/Libs/installations/api/installation.api'

import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useNavigate } from 'react-router-dom'
import { NewInstallationFormModel } from 'src/Libs/installations/models/installation.model'
import { NewInstallationForm } from 'src/Libs/installations'
import { usePortalMutation } from 'src/Libs/api'

export interface NewInstallationModalProps {
  onClose?: () => void
  place: number
}

class PlannedInstallationDefaultsError extends Error {
  constructor(readonly installationId: number, readonly originalError: unknown) {
    super('Failed to save planned installation EM360 defaults')
  }
}

const NewInstallationModal: React.FC<NewInstallationModalProps> = ({ onClose, place }) => {
  const { initialValues, validationSchema } = useCreateNewInstallationFormInitializer()
  const { t } = useCustomTranslation()
  const authFetch = useAuthFetchWithResponse()
  const { enqueueErrorStack, enqueueSuccessStack } = useSnackbar()
  const navigate = useNavigate()
  const { saveDefaults: saveEm360Defaults } = useSavePlannedInstallationEm360Defaults()

  const methods = useForm<NewInstallationFormModel>({
    defaultValues: initialValues,
    resolver: zodResolver(validationSchema),
    mode: 'onBlur',
  })

  const { mutate: createInstallation, isPending } = usePortalMutation({
    mutationFn: async (payload: NewInstallationFormModel) => {
      const result = await createNewInstallation(payload, authFetch)

      if (result.hasErrors) {
        throw result.error || new Error('Could not create installation')
      }

      if (result.value === null) {
        throw new Error('Installation creation returned no installation ID')
      }

      try {
        await saveEm360Defaults(result.value)
      } catch (error) {
        throw new PlannedInstallationDefaultsError(result.value, error)
      }

      return result
    },
    onSuccess: result => {
      enqueueSuccessStack(t('create-new-installation.success-message'))
      onClose?.()
      navigate(`/installation/${result}/configuration/general`)
    },
    onError: error => {
      if (error instanceof PlannedInstallationDefaultsError) {
        enqueueErrorStack(t('installation-configuration.general-settings.save.error'))
        console.error('Failed to save planned installation EM360 defaults', error.originalError)
        onClose?.()
        navigate(`/installation/${error.installationId}/configuration/general`)
        return
      }

      enqueueErrorStack(t('create-new-installation.error-message'))
      console.error('Failed to create installation', error)
    },
  })

  const onSubmit = (data: NewInstallationFormModel): void => {
    createInstallation(data)
  }

  return (
    <FormProvider {...methods}>
      <Dialog
        title={'create-new-installation.form.title'}
        onClose={onClose}
        place={place}
        actions={{
          primary: {
            name: 'common.save',
            disabled: isPending,
            onClick: methods.handleSubmit(onSubmit),
          },
          secondary: {
            name: 'common.cancel',
            onClick: () => {
              onClose?.()
            },
          },
        }}
        maxWidth="sm"
      >
        <Stack gap={2}>
          <SettingInfoBox description={'create-new-installation.info-text'} />
          <NewInstallationForm />
        </Stack>
      </Dialog>
    </FormProvider>
  )
}

export default NewInstallationModal
